/**
 * The four reports' URL state — `?period=&from=&to=&work_type=&page=` (+ the Parties
 * report's `q`, `sort`), read unvalidated and written by the controls in `ReportFrame`.
 *
 * A hook in its own module (not in the frame) so the frame file exports only a component —
 * the `react-refresh/only-export-components` rule the rest of the app keeps at its 31-warning
 * baseline.
 */

import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { readPeriodQuery } from '../dashboards/periods';
import type { PartiesFilters, ReportFilters } from './api';

export const PER_PAGE = 50;

function blankToUndefined(value: string | null): string | undefined {
  return value === null || value === '' ? undefined : value;
}

export function useReportUrl(): {
  filters: ReportFilters;
  partiesFilters: PartiesFilters;
  workType: string;
  setWorkType: (value: string | null) => void;
  setParam: (key: string, value: string | null) => void;
  goToPage: (page: number) => void;
} {
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);
  const workType = params.get('work_type') ?? '';

  const filters = useMemo<ReportFilters>(() => {
    const period = readPeriodQuery(params);
    return {
      period: period.period,
      from: period.from,
      to: period.to,
      work_type: blankToUndefined(params.get('work_type')),
      page,
      perPage: PER_PAGE,
    };
  }, [params, page]);

  const partiesFilters = useMemo<PartiesFilters>(
    () => ({
      ...filters,
      q: blankToUndefined(params.get('q')),
      city: blankToUndefined(params.get('city')),
      sort: blankToUndefined(params.get('sort')),
    }),
    [filters, params],
  );

  /** Filters REPLACE history and reset paging; paging PUSHES. */
  const setParam = (key: string, value: string | null): void => {
    const next = new URLSearchParams(params);
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
    next.delete('page');
    setParams(next, { replace: true });
  };

  const goToPage = (next: number): void => {
    const patched = new URLSearchParams(params);
    if (next <= 1) patched.delete('page');
    else patched.set('page', String(next));
    setParams(patched);
  };

  return {
    filters,
    partiesFilters,
    workType,
    setWorkType: (value) => setParam('work_type', value),
    setParam,
    goToPage,
  };
}
